(function () {
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  async function loadPdfJs(root) {
    if (window.pdfjsLib) {
      return window.pdfjsLib;
    }

    var pdfJsUrl = root.dataset.pdfjsUrl;
    var workerSrc = root.dataset.pdfjsWorkerUrl;

    if (!pdfJsUrl || !workerSrc) {
      throw new Error("Missing local PDF.js asset URLs.");
    }

    if (!window.__a2zCatalogPdfJsPromise) {
      window.__a2zCatalogPdfJsPromise = import(pdfJsUrl).then(function (module) {
        var pdfjsLib = module && module.GlobalWorkerOptions ? module : module.default;
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
        window.pdfjsLib = pdfjsLib;
        return pdfjsLib;
      });
    }

    return window.__a2zCatalogPdfJsPromise;
  }

  function CatalogViewer(root) {
    this.root = root;
    this.pdfUrl = root.dataset.pdfUrl;
    this.startPage = parseInt(root.dataset.startPage || "1", 10);
    this.maxZoom = parseFloat(root.dataset.maxZoom || "2.5");
    this.minZoom = parseFloat(root.dataset.minZoom || "0.75");
    this.zoomStep = parseFloat(root.dataset.zoomStep || "0.2");
    this.page = Math.max(this.startPage, 1);
    this.scale = this.minZoom + this.zoomStep;
    this.pdf = null;
    this.renderNonce = 0;

    this.prevButton = root.querySelector("[data-catalog-prev]");
    this.nextButton = root.querySelector("[data-catalog-next]");
    this.hoverPrevButton = root.querySelector("[data-catalog-prev-hover]");
    this.hoverNextButton = root.querySelector("[data-catalog-next-hover]");
    this.zoomInButton = root.querySelector("[data-catalog-zoom-in]");
    this.zoomOutButton = root.querySelector("[data-catalog-zoom-out]");
    this.fullscreenButton = root.querySelector("[data-catalog-fullscreen]");
    this.pageIndicator = root.querySelector("[data-catalog-page-indicator]");
    this.pageInput = root.querySelector("[data-catalog-page-input]");
    this.totalPages = root.querySelector("[data-catalog-total-pages]");
    this.status = root.querySelector("[data-catalog-status]");
    this.loading = root.querySelector("[data-catalog-loading]");
    this.canvasWrap = root.querySelector("[data-catalog-canvas-wrap]");
    this.leftPage = root.querySelector("[data-catalog-page-slot='left']");
    this.rightPage = root.querySelector("[data-catalog-page-slot='right']");
    this.leftCanvas = root.querySelector("[data-catalog-canvas='left']");
    this.rightCanvas = root.querySelector("[data-catalog-canvas='right']");

    this.handleResize = this.handleResize.bind(this);
    this.handleFullscreen = this.handleFullscreen.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  CatalogViewer.prototype.init = async function () {
    if (!this.pdfUrl) {
      this.setStatus("Add your PDF URL in the Catalog Viewer section settings.");
      this.root.dataset.catalogState = "error";
      return;
    }

    try {
      this.setStatus("Loading catalog...");
      this.root.dataset.catalogState = "loading";

      var pdfjsLib = await loadPdfJs(this.root);
      var loadingTask = pdfjsLib.getDocument({
        url: this.pdfUrl,
        withCredentials: false,
        useSystemFonts: true
      });

      this.pdf = await loadingTask.promise;
      this.page = clamp(this.normalizePage(this.page), 1, this.pdf.numPages);
      this.totalPages.textContent = this.pdf.numPages;
      this.root.dataset.catalogState = "ready";

      this.bindEvents();
      await this.renderSpread();
      this.setStatus("");
    } catch (error) {
      console.error("Catalog viewer failed to load PDF", error);
      this.root.dataset.catalogState = "error";
      this.setStatus("We couldn't load this PDF. Double-check the URL and confirm the file is publicly accessible.");
    }
  };

  CatalogViewer.prototype.bindEvents = function () {
    this.prevButton.addEventListener("click", this.goToPreviousSpread.bind(this));
    this.nextButton.addEventListener("click", this.goToNextSpread.bind(this));
    this.hoverPrevButton.addEventListener("click", this.goToPreviousSpread.bind(this));
    this.hoverNextButton.addEventListener("click", this.goToNextSpread.bind(this));
    this.zoomInButton.addEventListener("click", this.zoomIn.bind(this));
    this.zoomOutButton.addEventListener("click", this.zoomOut.bind(this));
    this.fullscreenButton.addEventListener("click", this.toggleFullscreen.bind(this));

    this.pageInput.addEventListener("change", this.handlePageInput.bind(this));
    this.pageInput.addEventListener("keydown", this.handlePageInputKeydown.bind(this));

    window.addEventListener("resize", this.handleResize);
    document.addEventListener("fullscreenchange", this.handleFullscreen);
    document.addEventListener("keydown", this.handleKeydown);
  };

  CatalogViewer.prototype.handleKeydown = function (event) {
    var target = event.target;

    if (target && /input|textarea|select/i.test(target.tagName)) {
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.goToPreviousSpread();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.goToNextSpread();
    }
  };

  CatalogViewer.prototype.normalizePage = function (page) {
    return clamp(page, 1, this.pdf ? this.pdf.numPages : page);
  };

  CatalogViewer.prototype.getSpread = function () {
    return { left: null, right: this.page };
  };

  CatalogViewer.prototype.handleResize = function () {
    if (!this.pdf) {
      return;
    }

    clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(this.renderSpread.bind(this), 120);
  };

  CatalogViewer.prototype.handleFullscreen = function () {
    this.root.classList.toggle("is-fullscreen", document.fullscreenElement === this.root);
  };

  CatalogViewer.prototype.handlePageInput = function () {
    var nextPage = parseInt(this.pageInput.value, 10);
    if (!Number.isFinite(nextPage)) {
      this.pageInput.value = this.page;
      return;
    }

    this.goToPage(nextPage);
  };

  CatalogViewer.prototype.handlePageInputKeydown = function (event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.handlePageInput();
    }
  };

  CatalogViewer.prototype.goToPreviousSpread = function () {
    this.goToPage(this.page - 1);
  };

  CatalogViewer.prototype.goToNextSpread = function () {
    this.goToPage(this.page + 1);
  };

  CatalogViewer.prototype.goToPage = async function (nextPage) {
    if (!this.pdf) {
      return;
    }

    this.page = clamp(this.normalizePage(nextPage), 1, this.pdf.numPages);
    await this.renderSpread();
  };

  CatalogViewer.prototype.zoomIn = async function () {
    this.scale = clamp(this.scale + this.zoomStep, this.minZoom, this.maxZoom);
    await this.renderSpread();
  };

  CatalogViewer.prototype.zoomOut = async function () {
    this.scale = clamp(this.scale - this.zoomStep, this.minZoom, this.maxZoom);
    await this.renderSpread();
  };

  CatalogViewer.prototype.toggleFullscreen = async function () {
    if (document.fullscreenElement === this.root) {
      await document.exitFullscreen();
      return;
    }

    if (this.root.requestFullscreen) {
      await this.root.requestFullscreen();
    }
  };

  CatalogViewer.prototype.setStatus = function (message) {
    this.status.textContent = message || "";
    this.loading.hidden = !message;
  };

  CatalogViewer.prototype.updateControls = function () {
    var spread = this.getSpread();
    var label = spread.right || spread.left || 1;

    this.pageIndicator.textContent = label;
    this.pageInput.value = this.page;
    this.prevButton.disabled = this.page <= 1;
    this.nextButton.disabled = (spread.right || spread.left || 1) >= this.pdf.numPages;
    this.hoverPrevButton.disabled = this.prevButton.disabled;
    this.hoverNextButton.disabled = this.nextButton.disabled;
    this.zoomOutButton.disabled = this.scale <= this.minZoom;
    this.zoomInButton.disabled = this.scale >= this.maxZoom;
  };

  CatalogViewer.prototype.clearCanvas = function (canvas) {
    var context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = 0;
    canvas.height = 0;
    canvas.style.width = "0px";
    canvas.style.height = "0px";
  };

  CatalogViewer.prototype.getRenderScale = function (pageNumber) {
    return clamp(this.scale, this.minZoom, this.maxZoom);
  };

  CatalogViewer.prototype.renderIntoCanvas = async function (pageNumber, canvas, availableWidth) {
    if (!pageNumber) {
      this.clearCanvas(canvas);
      return;
    }

    var page = await this.pdf.getPage(pageNumber);
    var unscaledViewport = page.getViewport({ scale: 1 });
    var availableHeight = Math.max(this.canvasWrap.clientHeight - 20, 260);
    var fitWidthScale = availableWidth / unscaledViewport.width;
    var fitHeightScale = availableHeight / unscaledViewport.height;
    var fitScale = Math.min(fitWidthScale, fitHeightScale);
    var viewport = page.getViewport({ scale: fitScale * this.getRenderScale(pageNumber) });
    var pixelRatio = window.devicePixelRatio || 1;
    var context = canvas.getContext("2d");

    canvas.width = Math.floor(viewport.width * pixelRatio);
    canvas.height = Math.floor(viewport.height * pixelRatio);
    canvas.style.width = Math.floor(viewport.width) + "px";
    canvas.style.height = Math.floor(viewport.height) + "px";

    await page.render({
      canvasContext: context,
      viewport: viewport,
      transform: pixelRatio !== 1 ? [pixelRatio, 0, 0, pixelRatio, 0, 0] : null
    }).promise;
  };

  CatalogViewer.prototype.renderSpread = async function () {
    if (!this.pdf) {
      return;
    }

    var nonce = ++this.renderNonce;

    try {
      this.setStatus("Rendering pages...");
      this.updateControls();

      var spread = this.getSpread();
      this.leftPage.classList.add("is-empty");
      this.root.classList.add("is-single-page");
      this.root.classList.remove("is-cover-page");
      this.rightPage.classList.remove("is-cover");

      var wrapWidth = Math.max(this.canvasWrap.clientWidth - 28, 260);
      var spreadWidth = wrapWidth;

      await Promise.all([
        this.renderIntoCanvas(spread.left, this.leftCanvas, spreadWidth),
        this.renderIntoCanvas(spread.right, this.rightCanvas, spreadWidth)
      ]);

      if (nonce !== this.renderNonce) {
        return;
      }

      this.setStatus("");
    } catch (error) {
      console.error("Catalog viewer failed to render spread", error);
      this.root.dataset.catalogState = "error";
      this.setStatus("We couldn't render this spread. Try refreshing or opening the PDF directly.");
    }
  };

  document.addEventListener("DOMContentLoaded", function () {
    var viewers = document.querySelectorAll("[data-a2z-catalog-viewer]");
    viewers.forEach(function (viewerElement) {
      var viewer = new CatalogViewer(viewerElement);
      viewer.init();
    });
  });
})();
